import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import PageMeta from "../../components/common/PageMeta";
import api from "../../lib/api";
import { cccdAdmin } from "@/lib/api/cccdAdminClient";
import { TourButton } from "../../components/admin/TourButton";
import { adminVisibilityTourSteps } from "../../lib/tours/adminVisibilityTour";
import { getCurrentUser, hasCurrentPolicy, type PermissionKey } from "../../lib/auth";

// Inicio por rol: cada bloque se muestra solo si el perfil tiene el permiso de la pantalla a la
// que lleva (las rutas igual lo validan en ProtectedRoute; esto evita ofrecer enlaces muertos).
type Shortcut = { label: string; hint: string; to: string; policy: PermissionKey };

const SHORTCUTS: Shortcut[] = [
  { label: "Check-in / Asistencia", hint: "Escanear boletos en la puerta del evento", to: "/cccd/checkin", policy: "cccd:checkin:view" },
  { label: "Mi grupo", hint: "Seguimiento de los estudiantes de tu grupo", to: "/instructor/my-group", policy: "academy:my-group:view" },
  { label: "Entregas", hint: "Calificar lo que los estudiantes enviaron", to: "/instructor/submissions", policy: "academy:submissions:view" },
  { label: "Lista de espera TRA", hint: "Revisar quién espera cupo en el club", to: "/tra/waitlist", policy: "tripoli:waitlist:view" },
];

type QuickLink = { label: string; to: string; policy: PermissionKey };

const QUICK_LINKS: QuickLink[] = [
  { label: "Panel del instructor", to: "/instructor/hub", policy: "academy:instructor-hub:view" },
  { label: "Organizaciones", to: "/programs/institutions", policy: "academy:institutions:view" },
  { label: "Estudiantes", to: "/instructor/students", policy: "academy:students:view" },
  { label: "Videos", to: "/instructor/videos", policy: "academy:videos:view" },
  { label: "Certificados", to: "/instructor/certificates", policy: "academy:certificates:view" },
];

type AcademyStats = {
  total_students?: number;
  pending_submissions?: number;
  average_progress?: number;
  certificates_issued?: number;
};

type HomeMetricTone = "neutral" | "good" | "warning";

type HomeMetric = {
  label: string;
  value: string;
  hint: string;
  tone: HomeMetricTone;
  to: string;
  policy: PermissionKey;
};

const TONE_VALUE_CLASS: Record<HomeMetricTone, string> = {
  neutral: "text-gray-900 dark:text-white",
  good: "text-success-600 dark:text-success-500",
  warning: "text-warning-600 dark:text-orange-400",
};

const TONE_DOT_CLASS: Record<HomeMetricTone, string> = {
  neutral: "bg-gray-300 dark:bg-gray-600",
  good: "bg-success-500",
  warning: "bg-warning-500",
};

function HomeMetricCard({ metric }: { metric: HomeMetric }) {
  return (
    <Link
      to={metric.to}
      className="rounded-2xl border border-gray-200 bg-white p-4 transition-colors hover:border-brand-300 dark:border-gray-800 dark:bg-white/[0.03] dark:hover:border-brand-700"
    >
      <div className="flex items-center gap-1.5">
        <span className={`size-1.5 shrink-0 rounded-full ${TONE_DOT_CLASS[metric.tone]}`} aria-hidden="true" />
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {metric.label}
        </p>
      </div>
      <p className={`mt-1.5 text-3xl font-bold ${TONE_VALUE_CLASS[metric.tone]}`}>{metric.value}</p>
      <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-500">{metric.hint}</p>
    </Link>
  );
}

export default function Home() {
  // Solo se piden los datos de los dominios que el perfil puede ver: un instructor no debe
  // disparar (ni recibir un 403 convertido en "0 pendientes") de los equipos CCCD.
  const canAcademyStats = hasCurrentPolicy("academy:submissions:view") || hasCurrentPolicy("academy:students:view");
  const canCccdTeams = hasCurrentPolicy("cccd:panel:view");

  const { data: academyDash, isLoading: academyLoading } = useQuery({
    queryKey: ["home", "academy-dashboard"],
    queryFn: () => api.get("/admin/dashboard").then((res) => res.data.data ?? res.data),
    enabled: canAcademyStats,
  });

  // Cada KPI de equipos es el total real que informa el servidor para ese estado (limit 1: solo
  // interesa `total`), y su enlace abre el listado filtrado por ese mismo estado.
  const teamsTotal = (status: string) => ({
    queryKey: ["home", "cccd-teams-total", status],
    queryFn: () => cccdAdmin.listTeams({ status, limit: 1 }).then((res) => res.total),
    enabled: canCccdTeams,
  });
  const { data: negotiatingTeams, isLoading: negotiatingLoading } = useQuery(teamsTotal("negotiating"));
  const { data: paymentPendingTeams, isLoading: paymentLoading } = useQuery(teamsTotal("payment_pending"));
  const { data: activeTeams, isLoading: activeLoading } = useQuery(teamsTotal("active"));

  const academyStats: AcademyStats = academyDash?.stats ?? academyDash ?? {};

  const loading = academyLoading || negotiatingLoading || paymentLoading || activeLoading;

  const allMetrics: HomeMetric[] = [
    {
      label: "Entregas por calificar",
      value: String(academyStats.pending_submissions ?? 0),
      hint: "Bloquean el avance de esos estudiantes hasta que un instructor las revise",
      tone: (academyStats.pending_submissions ?? 0) > 0 ? "warning" : "good",
      to: "/instructor/submissions",
      policy: "academy:submissions:view",
    },
    {
      label: "Equipos en negociación",
      value: String(negotiatingTeams ?? 0),
      hint: "Aún sin acuerdo: sin seguimiento pueden perder el cupo",
      tone: (negotiatingTeams ?? 0) > 0 ? "warning" : "good",
      to: "/cccd",
      policy: "cccd:panel:view",
    },
    {
      label: "Equipos con pago pendiente",
      value: String(paymentPendingTeams ?? 0),
      hint: "Esperando el pago: conviene dar seguimiento antes de que pierdan el cupo",
      tone: (paymentPendingTeams ?? 0) > 0 ? "warning" : "good",
      to: "/cccd",
      policy: "cccd:panel:view",
    },
    {
      label: "Equipos CCCD confirmados",
      value: String(activeTeams ?? 0),
      hint: "Listos para el evento, sin pendientes",
      tone: "good",
      to: "/cccd",
      policy: "cccd:panel:view",
    },
    {
      label: "Progreso promedio academia",
      value: academyStats.average_progress != null ? `${Math.round(academyStats.average_progress)}%` : "—",
      hint: "Avance de estudiantes activos en sus cursos",
      tone: "neutral",
      to: "/instructor/students",
      policy: "academy:students:view",
    },
  ];
  const metrics = allMetrics.filter((m) => hasCurrentPolicy(m.policy));
  const shortcuts = SHORTCUTS.filter((s) => hasCurrentPolicy(s.policy));
  const quickLinks = QUICK_LINKS.filter((l) => hasCurrentPolicy(l.policy));
  const showAcademyCard = hasCurrentPolicy("academy:courses:view");
  const showCccdCard = hasCurrentPolicy("cccd:panel:view");

  // Saludo según la hora y un resumen de lo que espera a este perfil, calculado con los mismos datos
  // de los KPIs. Solo cuenta lo que el perfil puede ver y lo que ya cargó (sin datos no dice "todo al día").
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";
  const firstName = getCurrentUser()?.name?.trim().split(/\s+/)[0];
  const tracked: { noun: string; pending: number }[] = [];
  if (hasCurrentPolicy("academy:submissions:view") && academyDash !== undefined) {
    tracked.push({ noun: "entregas por calificar", pending: academyStats.pending_submissions ?? 0 });
  }
  if (canCccdTeams && negotiatingTeams !== undefined) tracked.push({ noun: "equipos en negociación", pending: negotiatingTeams });
  if (canCccdTeams && paymentPendingTeams !== undefined) tracked.push({ noun: "equipos con pago pendiente", pending: paymentPendingTeams });
  const withPending = tracked.filter((t) => t.pending > 0);
  const noneLabel = tracked.length > 1 ? `${tracked.slice(0, -1).map((t) => t.noun).join(", ")} ni ${tracked[tracked.length - 1].noun}` : tracked[0]?.noun;
  const summary = loading || tracked.length === 0
    ? null
    : withPending.length > 0
      ? `Pendiente hoy: ${withPending.map((t) => `${t.pending} ${t.noun}`).join(" · ")}.`
      : `Todo al día: sin ${noneLabel}.`;

  return (
    <>
      <PageMeta
        title="Inicio | SateLab Gestión"
        description="Panel administrativo central para academia y subproyectos"
      />
      <section className="flex flex-col gap-6">

        {/* Hero */}
        <div data-tour="home-hero" className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">
                {firstName ? `${greeting}, ${firstName}` : greeting}
              </h1>
              {summary ? (
                <p role="status" className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                  {summary}
                </p>
              ) : null}
            </div>
            <TourButton steps={adminVisibilityTourSteps} />
          </div>
        </div>

        {/* Atajos de tu rol: lo que usas a diario, a un toque */}
        {shortcuts.length > 0 && (
          <nav aria-label="Atajos de tu rol" className="order-first grid grid-cols-1 gap-3 sm:grid-cols-2 lg:order-none xl:grid-cols-4">
            {shortcuts.map((shortcut, i) => (
              <Link
                key={shortcut.to}
                to={shortcut.to}
                className={`flex min-h-16 flex-col justify-center rounded-2xl border p-4 transition-colors focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 ${
                  i === 0
                    ? "border-brand-500 bg-brand-500 text-white hover:bg-brand-600"
                    : "border-gray-200 bg-white text-gray-900 hover:border-brand-300 dark:border-gray-800 dark:bg-white/[0.03] dark:text-white dark:hover:border-brand-700"
                }`}
              >
                <span className="text-base font-semibold">{shortcut.label}</span>
                <span className={`mt-0.5 text-sm ${i === 0 ? "text-white/85" : "text-gray-500 dark:text-gray-400"}`}>{shortcut.hint}</span>
              </Link>
            ))}
          </nav>
        )}

        {/* KPIs que importan hoy */}
        {metrics.length > 0 && (
        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-500">
            Qué necesita tu atención hoy
          </h2>
          {loading ? (
            <div data-tour="home-metrics" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-[110px] animate-pulse rounded-2xl border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-white/[0.03]"
                />
              ))}
            </div>
          ) : (
            <div data-tour="home-metrics" className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {metrics.map((metric) => (
                <HomeMetricCard key={metric.label} metric={metric} />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Proyectos */}
        {(showAcademyCard || showCccdCard) && (
        <div>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-500">
            Proyectos
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

            {/* Academia */}
            {showAcademyCard && (
            <article data-tour="home-project-academia" className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Academia</h3>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                LMS aeroespacial — cursos, semilleros institucionales, programas educativos, videos y certificados.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/academy/courses"
                  className="inline-flex rounded-lg bg-brand-500 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-600"
                >
                  Cursos
                </Link>
                <Link
                  to="/programs/seedbeds"
                  className="inline-flex rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.05]"
                >
                  Semilleros
                </Link>
                <Link
                  to="/programs/programs"
                  className="inline-flex rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.05]"
                >
                  Programas
                </Link>
              </div>
            </article>
            )}

            {/* CCCD */}
            {showCccdCard && (
            <article data-tour="home-project-cccd" className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">CCCD</h3>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                Concurso Colombiano de Cohetería y Diseño — equipos, inscripciones, pagos y eventos del concurso.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/cccd"
                  className="inline-flex rounded-lg bg-brand-500 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-600"
                >
                  Panel
                </Link>
                <Link
                  to="/cccd/events"
                  className="inline-flex rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.05]"
                >
                  Nacionales
                </Link>
              </div>
            </article>
            )}

          </div>
        </div>
        )}

        {/* Accesos rápidos */}
        {quickLinks.length > 0 && (
        <div data-tour="home-quick-links" className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-500">
            Accesos rápidos
          </h2>
          <div className="flex flex-wrap gap-2">
            {quickLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 hover:border-brand-500 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-brand-500 dark:hover:text-brand-400"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        )}


      </section>
    </>
  );
}
